import { useState, useEffect, useMemo } from 'react';
import { getFilterFunction } from '../utils/filterFunctions';
import { sortData } from '../utils/sortUtils';
import hash from 'object-hash'; // or any fast lib

const STORAGE_PREFIX = 'column_filters_';

/**
 * CRM Data Filter Chain Manager
 * 
 * Domain Logic:
 * - Manages cascading filters where each column's available values depend on previous filters
 * - Persists filter state across browser sessions with data change detection
 * - Handles CRM data edge cases (null values, custom selectors, dynamic data)
 * - Provides smart filter reset when underlying data changes
 * - Maintains filter order and sort state for complex CRM queries
 * 
 * @param {Object} params - Hook parameters
 * @param {Array<Object>} params.data - CRM data rows to filter
 * @param {Array<Object>} params.columns - Column definitions with selectors
 * @param {string} [params.storageKey] - Unique key for filter persistence
 * @param {Object} [params.initialState] - Initial filter state
 * @param {any} [params.resetTrigger] - Trigger to reset filters
 * @returns {Object} Filter management state and functions
 */

/**
 * Creates a filter chain that tracks dependencies between columns
 * Each column's available values are filtered by previous column selections
 */
const createFilterChain = (columns) => {
  const chain = new Map();
  
  // Initialize chain with column order
  columns.forEach((col, index) => {
    const previousFilters = new Set(columns.slice(0, index).map(c => c.id));
    
    chain.set(col.id, {
      index,
      filter: null,
      availableValues: new Set(),
      previousFilters
    });
  });

  return chain;
};

// Export the clearAllFilters function so it can be used directly
export const clearAllColumnFilters = () => {  
     Object.keys(localStorage).forEach(key => {
      if (key.startsWith(`${STORAGE_PREFIX}`)) {
        localStorage.removeItem(key);
      }
    });

};

export default function useColumnFilters({
  data=[],
  columns,
  storageKey = null,
  initialState = {},
  resetTrigger
}) {
  console.log('useColumnFilters initialized with storageKey:', storageKey, 'columns:', columns,data);

  // State for filter management
  /** @type {Object} Current filter state by column ID */
  const [filters, setFilters] = useState(initialState);
  /** @type {string|null} Currently sorted column */
  const [sortBy, setSortBy] = useState(null);
  /** @type {string|null} Sort direction (asc/desc) */
  const [sortOrder, setSortOrder] = useState(null);
  /** @type {Array<Object>} Current data being filtered */
  const [currentData, setCurrentData] = useState(data || []);
  /** @type {Array<string>} Order of applied filters */
  const [filterOrder, setFilterOrder] = useState([]);
  /** @type {boolean} Whether filter order is ready */
  const [filterOrderReady, setFilterOrderReady] = useState(!storageKey);
  /** @type {Map} Filter chain for cascading dependencies */
  const [filterChain, setFilterChain] = useState(() => createFilterChain(columns));

if (!data || !data.length) {
  data = []
}
  // Load filters from localStorage
  
  // Clean filters when data changes (remove values no longer present)
  useEffect(() => {
    if (!currentData || currentData.length === 0) return;
    const updated = { ...filters };
    let changed = false;
    columns.forEach(col => {
      const existing = filters[col.id];
      if (!existing || !existing.checkboxFilters?.length) return;
      const allValues = new Set(
        currentData.map(row => {
            const val = col.selector ? col.selector(row) : row[col.id];
            return val === '' || val == null ? '(None)' : val;
        })
      );
      const valid = existing.checkboxFilters.filter(v => allValues.has(v));
      if (valid.length !== existing.checkboxFilters.length) {
        updated[col.id] = { ...existing, checkboxFilters: valid };
        changed = true;
      }
    });
    if (changed) setFilters(updated);
  }, [currentData, columns]);

  // Add reset effect
// If you need the filterChain logic (only if columns can change dynamically)
useEffect(() => {
  setFilterChain(createFilterChain(columns));
}, [columns]);

// Load all state from storage when the hook mounts or storageKey changes
useEffect(() => {
  if (!storageKey) return;
  const savedState = localStorage.getItem(getStoragePrefix());
  if (savedState) {
    try {
      const { filters: savedFilters, sortBy: savedSortBy, sortOrder: savedSortOrder, filterOrder: savedFilterOrder } = JSON.parse(savedState);
      setFilters(savedFilters || {});
      setSortBy(savedSortBy || null);
      setSortOrder(savedSortOrder || null);
      setFilterOrder(savedFilterOrder || []);
    } catch (e) {
      // Optionally log error
    }
  }
}, [storageKey]);

// Save all state to storage whenever it changes
useEffect(() => {
  if (storageKey) {
    localStorage.setItem(
      getStoragePrefix(),
      JSON.stringify({ filters, sortBy, sortOrder, filterOrder })
    );
  }
}, [filters, sortBy, sortOrder, filterOrder, storageKey]);

// Smart auto-reset: Clear filters if the actual data changes
const getDataFingerprint = (rows) =>hash(rows.slice(0, 100));

useEffect(() => {
  if (!data || !data.length || !storageKey) return;
  const fingerprintKey = `${storageKey}__fingerprint`;
  const newFingerprint = getDataFingerprint(data);
  const previousFingerprint = localStorage.getItem(getStoragePrefix(fingerprintKey)) || '';

  if (newFingerprint !== previousFingerprint) {
    console.log('Data changed, resetting filters and sort order');
    
    localStorage.setItem(getStoragePrefix(fingerprintKey), newFingerprint);
    setFilters({});
    setSortBy(null);
    setSortOrder(null);
    setFilterOrder([]);
  }

  setCurrentData(data);
}, [data, columns, storageKey]);

  const getStoragePrefix = (item=null) => {
    return item ? `${STORAGE_PREFIX}${storageKey}_${item}` : `${STORAGE_PREFIX}${storageKey}`;
  }

  /**
   * Calculates available values for each column based on filter chain
   * Each column's available values are filtered by previous column selections
   */
  const calculateAvailableValues = useMemo(() => {
    const result = {};
    // Helper function to get cell value using column selector
    const getCellValue = (row, column) => {
      const val = column.selector ? column.selector(row) : row[column.id];
      return val === '' || val == null ? '(None)' : val;
    };

    // Helper function to apply a single filter
    const applyFilter = (data, filter, column) => {
      if (!filter) return data;

      let filteredData = [...data];

      // Apply checkbox filters if exist
      if (filter.checkboxFilters?.length > 0) {
        filteredData = filteredData.filter(row => {
          const val = getCellValue(row, column);
          return filter.checkboxFilters.includes(val);
        });
      }

      return filteredData;
    };
    
    columns.forEach(col => {
      const chainInfo = filterChain.get(col.id);
      if (!chainInfo) return;
      
      // Start with original data
      let availableData = [...data];      // Apply filters from previous columns
      chainInfo.previousFilters.forEach(prevColId => {
        const prevCol = columns.find(c => c.id === prevColId);
        if (!prevCol) return;

        const prevFilter = filters[prevColId];
        if (!prevFilter) return;

        availableData = applyFilter(availableData, prevFilter, prevCol);
      });

      // Extract unique values for current column
      result[col.id] = [...new Set(
        availableData.map(row => getCellValue(row, col))
      )];
    });

    return result;
  }, [data, columns, filters, filterChain]);

  /**
   * Handles filter changes and updates filter order
   * Manages cascading filter dependencies and sort state
   */
  const handleFilterChange = (colId, textFilter, checkboxFilters, newSortDirection) => {
    setFilters(prev => {
      const next = {
        ...prev,
        [colId]: { textFilter, checkboxFilters }
      };
            return next;
    });

    const isEmpty = !textFilter?.value && (!checkboxFilters || checkboxFilters.length === 0);

    setFilterOrder(prev => {
      let updatedOrder;
      
      if (isEmpty) {
        updatedOrder = prev.filter(id => id !== colId);
      } else if (!prev.includes(colId)) {
        updatedOrder = [...prev, colId];
      } else {
        updatedOrder = prev;
      }
        
      return updatedOrder;
    });
      
    if (newSortDirection) {
      setSortBy(colId);
      setSortOrder(newSortDirection);
    } else if (sortBy === colId) {
      setSortBy(null);
      setSortOrder(null);
    }
  };

  /**
   * Resets filters to new initial state
   */
  const resetWith = (newInitialState) => {
    setFilters(newInitialState);
    setSortBy(null);
    setSortOrder(null);
  };

  /**
   * Updates the data being filtered
   */
  const updateData = (newData) => {
    setCurrentData(newData || []);
  };

  /**
   * Computed filtered data with all filters and sorting applied
   * Handles CRM data edge cases like null values and custom selectors
   */
  const filteredData = useMemo(() => {
    if (!data || data.length === 0) return [];
    
    let result = [...data];
    
    // Apply filters
    result = result.filter(row =>
      columns.every(col => {
        const filter = filters[col.id];
        if (!filter) return true;

        const cell = (col.selector ? col.selector(row) : row[col.id] || '')?.toString();

        if (filter.checkboxFilters?.length > 0) {
          const val = col.selector ? col.selector(row) : row[col.id];
          const normalized = val === '' || val == null ? '(None)' : val;
          if (!filter.checkboxFilters.includes(normalized)) return false;
        }

        return true;
      })
    );

    return sortData(result, sortBy, sortOrder);
  }, [data, columns, filters, sortBy, sortOrder]);

  const clearStorageFilters = () => {
    if (!storageKey) return;
    Object.keys(localStorage).forEach(key => {
      if (key.startsWith(`${STORAGE_PREFIX}${storageKey}`)) {
        console.log(`Clearing filter from localStorage: ${key}`);
        // localStorage.removeItem(key);
      }
    });
  };
  
  return {
    /** @type {Object} Current filter state by column ID */
    filters,
    /** @type {string|null} Currently sorted column */
    sortBy,
    /** @type {string|null} Sort direction */
    sortOrder,
    /** @type {Array<Object>} Filtered and sorted data */
    filteredData,
    /** @type {Function} Handle filter changes */
    handleFilterChange,
    /** @type {Array<string>} Order of applied filters */
    filterOrder,
    /** @type {Function} Reset filters to new state */
    resetWith,
    /** @type {Function} Update data being filtered */
    updateData,
    /** @type {Object} Available values by column considering filter chain */
    uniqueValuesByColumn: calculateAvailableValues
  };
}